import React, { useEffect, useState } from "react";
import { api, API, tokenParam } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useConfig } from "../context/ConfigContext";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Save, FileText, Mail, Loader2, Recycle } from "lucide-react";
import { detectAllergens, aggregateAllergens } from "../lib/allergens";

const SECTIONS = ["Antipasto", "Primo", "Secondo", "Contorno", "Dolce"];
const today = () => new Date().toISOString().slice(0, 10);

export default function Menu() {
  const { canWrite, user } = useAuth();
  const { config } = useConfig();
  const [date, setDate] = useState(today());
  const [title, setTitle] = useState("");
  const [courses, setCourses] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [archive, setArchive] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [closeSvc, setCloseSvc] = useState(null);
  const isMarpolUser = user?.role === "chef" || user?.role === "comandante";
  const doCloseService = async () => {
    const covers = parseInt(closeSvc.covers) || 0;
    const m3 = +(covers * 0.0025).toFixed(3);
    const kg = +(covers * 0.35).toFixed(1);
    try {
      await api.post("/marpol/inventory/adjust", { waste_category: "B_food_waste", delta_m3: m3, delta_kg: kg, storage_location: "Da servizio cucina" });
      toast.success(`Servizio chiuso · registrati +${m3} m³ (${kg} kg) di rifiuti alimentari (cat. B)`);
      setCloseSvc(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Registrazione rifiuti non riuscita"); }
  };

  const load = () => {
    api.get(`/menu/${date}`).then((r) => {
      setTitle(r.data.title || "");
      setCourses(r.data.courses || []);
      setNotes(r.data.notes || "");
    }).catch(() => {});
  };
  useEffect(() => { load(); }, [date]);

  const loadArchive = () => api.get("/menus").then((r) => setArchive(r.data)).catch(() => {});
  useEffect(() => {
    loadArchive();
    api.get("/recipes").then((r) => setRecipes(r.data)).catch(() => {});
  }, []);

  const recipeById = (id) => recipes.find((r) => r.id === id);
  const courseText = (c) => {
    const r = recipeById(c.recipe_id);
    const ing = r ? (r.ingredients || []).map((x) => x.product).join(" ") : "";
    return `${c.dish || ""} ${ing}`;
  };

  const addCourse = () => setCourses([...courses, { section: "Antipasto", dish: "" }]);
  const setCourse = (i, k, v) => setCourses(courses.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const delCourse = (i) => setCourses(courses.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/menu/${date}`, { date, title, courses, notes });
      toast.success("Menù salvato");
      loadArchive();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const openPdf = async () => {
    try {
      if (canWrite) { await api.put(`/menu/${date}`, { date, title, courses, notes }); loadArchive(); }
      window.open(`${API}/menu/${date}/pdf?auth=${tokenParam()}`, "_blank");
    } catch (e) { toast.error("Impossibile aprire il PDF"); }
  };

  const sendToOwner = async () => {
    setSending(true);
    try {
      if (canWrite) await api.put(`/menu/${date}`, { date, title, courses, notes });
      const { data } = await api.post(`/menu/${date}/send-email`);
      toast.success(`Menù inviato a ${data.sent_to}`);
      loadArchive();
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); }
    finally { setSending(false); }
  };

  const allergens = aggregateAllergens(courses.map(courseText));
  const menuCost = courses.reduce((s, c) => s + (recipeById(c.recipe_id)?.cost_per_serving || 0), 0);
  const dateLabel = new Date(date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="space-y-6" data-testid="menu-page">
      <div className="no-print flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold text-navy">Menù del giorno</h1>
          <p className="mt-1 text-sm text-slate-500">Scrivi il menù per ospiti e armatore. Gli allergeni sono rilevati in automatico.</p>
        </div>
        <div className="flex gap-2">
          <input data-testid="menu-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
          {canWrite && <button data-testid="menu-save" onClick={save} disabled={saving} className="flex h-11 items-center gap-2 rounded-lg bg-navy px-4 font-semibold text-white hover:bg-navy-hover disabled:opacity-60"><Save className="h-4 w-4" /> Salva</button>}
          <button data-testid="menu-pdf" onClick={openPdf} className="flex h-11 items-center gap-2 rounded-lg border-2 border-slate-200 bg-white px-4 font-semibold text-navy hover:bg-slate-50"><FileText className="h-4 w-4" /> Apri PDF</button>
          {canWrite && <button data-testid="menu-send" onClick={sendToOwner} disabled={sending} className="flex h-11 items-center gap-2 rounded-lg bg-gold px-4 font-bold text-black hover:bg-gold-hover disabled:opacity-60">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Invia all'armatore</button>}
          {canWrite && isMarpolUser && <button data-testid="menu-close-service" onClick={() => setCloseSvc({ covers: config?.standard_crew || 8 })} className="flex h-11 items-center gap-2 rounded-lg border-2 border-emerald-500 px-4 font-semibold text-emerald-700 hover:bg-emerald-50"><Recycle className="h-4 w-4" /> Chiudi servizio</button>}
          <button data-testid="menu-print" onClick={() => window.print()} className="flex h-11 items-center gap-2 rounded-lg border-2 border-slate-200 bg-white px-4 font-semibold text-navy hover:bg-slate-50"><Printer className="h-4 w-4" /> Stampa</button>
        </div>
      </div>

      {closeSvc && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center" onClick={() => setCloseSvc(null)}>
          <div data-testid="close-service-modal" className="animate-slide-up w-full max-w-md rounded-t-2xl bg-white p-6 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head text-xl font-bold text-navy">Chiudi servizio</h3>
            <p className="mt-1 text-sm text-slate-500">Stima gli scarti alimentari del servizio e registrali tra i rifiuti MARPOL (categoria B). La stima usa circa 0,35 kg per coperto.</p>
            <label className="mt-4 block text-sm font-semibold text-navy">Coperti serviti</label>
            <input data-testid="close-service-covers" type="number" value={closeSvc.covers} onChange={(e) => setCloseSvc({ covers: e.target.value })} className="mt-1 h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
            <div className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">Stima scarti: <b>{(+(( parseInt(closeSvc.covers) || 0) * 0.0025)).toFixed(3)} m³</b> · <b>{(+(((parseInt(closeSvc.covers) || 0) * 0.35))).toFixed(1)} kg</b></div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setCloseSvc(null)} className="h-12 flex-1 rounded-lg border-2 border-slate-200 font-semibold text-navy">Annulla</button>
              <button data-testid="close-service-confirm" onClick={doCloseService} className="h-12 flex-1 rounded-lg bg-emerald-600 font-bold text-white hover:bg-emerald-700">Registra rifiuti</button>
            </div>
          </div>
        </div>
      )}

      {archive.length > 0 && (
        <div className="no-print rounded-2xl border border-slate-200 bg-white p-4" data-testid="menu-archive">
          <div className="mb-2 text-sm font-semibold text-navy">Menù salvati</div>
          <div className="flex flex-wrap gap-2">
            {archive.map((m) => (
              <button key={m.date} data-testid={`menu-open-${m.date}`} onClick={() => setDate(m.date)} className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${m.date === date ? "border-gold bg-gold/10 text-navy" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                {new Date(m.date + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} · {m.title || "Menù"}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Editor */}
      {canWrite && (
        <div className="no-print space-y-3 rounded-2xl border border-slate-200 bg-white p-6">
          <input data-testid="menu-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo (es. Cena a bordo)" className="h-11 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
          {courses.map((c, i) => {
            const al = detectAllergens(courseText(c));
            return (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <select data-testid={`menu-section-${i}`} value={c.section} onChange={(e) => setCourse(i, "section", e.target.value)} className="h-11 w-28 rounded-lg border-2 border-slate-200 px-2 outline-none focus:border-gold">
                  {SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {recipes.length > 0 && (
                  <select data-testid={`menu-recipe-${i}`} value={c.recipe_id || ""} onChange={(e) => { const r = recipes.find((x) => x.id === e.target.value); setCourses(courses.map((cc, idx) => (idx === i ? { ...cc, recipe_id: e.target.value, dish: r ? r.name : cc.dish } : cc))); }} className="h-11 w-40 rounded-lg border-2 border-slate-200 px-2 text-sm outline-none focus:border-gold">
                    <option value="">Da ricetta…</option>
                    {recipes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                )}
                <input data-testid={`menu-dish-${i}`} value={c.dish} onChange={(e) => setCourse(i, "dish", e.target.value)} placeholder="Nome del piatto (con ingredienti)" className="h-11 flex-1 rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
                <span className="min-w-[110px] text-xs text-amber-600">{al.map((a) => a.name).join(", ")}</span>
                <button data-testid={`menu-del-${i}`} onClick={() => delCourse(i)} className="rounded-lg bg-red-50 p-2 text-red-600 hover:bg-red-100"><Trash2 className="h-4 w-4" /></button>
              </div>
            );
          })}
          <button data-testid="menu-add-course" onClick={addCourse} className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-navy hover:bg-slate-200"><Plus className="h-4 w-4" /> Aggiungi portata</button>
          {menuCost > 0 && <div data-testid="menu-foodcost" className="text-sm text-slate-500">Food cost stimato del menù: <span className="font-semibold text-navy">€{menuCost.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> a coperto</div>}
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (opzionale)" rows={2} className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 outline-none focus:border-gold" />
        </div>
      )}

      {/* Anteprima stampabile */}
      <div id="menu-print" className="grid gap-6 rounded-2xl border border-navy/10 bg-white p-8 md:grid-cols-[1fr_240px]">
        <div>
          <div className="text-center">
            <img src="/logo.png" alt="logo" className="mx-auto h-16 w-16 rounded-xl" />
            <div className="mt-2 font-head text-sm uppercase tracking-widest text-gold">{config?.name || "Yacht Food Cost"}</div>
            <h2 className="mt-1 font-head text-3xl font-bold text-navy">{title || "Menù"}</h2>
            <div className="mt-1 text-sm capitalize text-slate-500">{dateLabel}</div>
          </div>
          <div className="mx-auto mt-8 max-w-md space-y-5">
            {courses.length === 0 && <p className="text-center text-slate-400">Nessun piatto inserito.</p>}
            {courses.map((c, i) => {
              const al = detectAllergens(c.dish);
              return (
                <div key={i} className="text-center">
                  <div className="font-head text-xs uppercase tracking-widest text-gold">{c.section}</div>
                  <div className="font-head text-lg text-navy">{c.dish}{al.length > 0 && <sup className="ml-1 text-[10px] text-slate-400">{al.map((a) => a.id).join(",")}</sup>}</div>
                </div>
              );
            })}
          </div>
          {notes && <p className="mt-8 text-center text-sm italic text-slate-500">{notes}</p>}
        </div>

        {/* Colonna laterale allergeni */}
        <aside data-testid="menu-allergens" className="rounded-xl bg-[#F4F1EA] p-4">
          <div className="font-head text-sm font-bold uppercase tracking-widest text-navy">Allergeni</div>
          <div className="mt-1 text-[11px] text-slate-400">Reg. UE 1169/2011</div>
          {allergens.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">Nessun allergene rilevato.</p>
          ) : (
            <ul className="mt-3 space-y-1.5">
              {allergens.map((a) => (
                <li key={a.id} className="flex items-center gap-2 text-sm text-navy">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-navy text-[10px] font-bold text-gold">{a.id}</span>
                  {a.name}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-[10px] leading-tight text-slate-400">Rilevamento automatico dagli ingredienti indicati. Verificare sempre in caso di intolleranze gravi.</p>
        </aside>
      </div>
    </div>
  );
}
